import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { Case, Visit, Child, Contract, Parent, ClientFile } from "@visitproof/types";
import { CaseService } from "../services/CaseService";
import { useAccount } from "./AccountContext";
import { useAuth } from "./AuthContext";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

// Re-type ClientFile as File for backward compatibility
type File = ClientFile;

interface Errors {
  list?: Error | null;
  details?: Error | null;
  create?: Error | null;
  update?: Error | null;
  delete?: Error | null;
}

interface Loadings {
  list?: boolean;
  details?: boolean;
  create?: boolean;
  update?: boolean;
  delete?: boolean;
}

interface CaseStatistics {
  total: number;
  open: number;
  closed: number;
  withParents: number;
  withChildren: number;
  withLocation: number;
}

interface CaseContextProps {
  loading: Loadings;
  error: Errors;
  parents: Parent[];
  childList: Child[];
  visits: Visit[];
  files: File[];
  contracts: Contract[];
  cases: Case[];
  selectedCase: Case | null;
  statistics: CaseStatistics;
  setSelectedCase: (caseId: string) => void;
  createCase: (
    caseData: Omit<
      Case,
      | "id"
      | "createdAt"
      | "updatedAt"
      | "users"
      | "children"
      | "accountId"
      | "createdBy"
      | "status"
    >,
  ) => Promise<string>;
  updateCase: (
    caseId: string,
    caseData: Partial<
      Omit<Case, "id" | "createdAt" | "updatedAt" | "users" | "children">
    >,
  ) => Promise<void>;
  deleteCaseById: (caseId: string) => Promise<void>;
  addFileToCase: (
    caseId: string,
    fileData: Omit<File, "id" | "createdAt" | "updatedAt" | "createdBy">,
  ) => Promise<string>;
  updateFileInCase: (
    caseId: string,
    fileId: string,
    updatedData: Partial<
      Omit<File, "id" | "createdAt" | "updatedAt" | "createdBy">
    >,
  ) => Promise<void>;
  deleteFileFromCase: (caseId: string, fileId: string) => Promise<void>;
  removeParentFromCase: (parentId: string) => Promise<void>;
  clearErrors: () => void;
  getCaseById: (caseId: string) => Case | undefined;
}

const CaseContext = createContext<CaseContextProps | undefined>(undefined);

export const useCase = (): CaseContextProps => {
  const context = useContext(CaseContext);
  if (!context) {
    throw new Error("useCase must be used within a CaseProvider");
  }
  return context;
};

export const CaseProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { account } = useAccount();

  const [visits, setVisits] = useState<Visit[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [parents, setParents] = useState<Parent[]>([]);
  const [childList, setChildList] = useState<Child[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [loading, setLoading] = useState<Loadings>({
    list: true,
    details: true,
    create: false,
    update: false,
    delete: false,
  });
  const [error, setError] = useState<Errors>({});

  // Memoized statistics
  const statistics = useMemo((): CaseStatistics => {
    return {
      total: cases.length,
      open: cases.filter(c => c.status === "open").length,
      closed: cases.filter(c => c.status === "closed").length,
      withParents: cases.filter(c => c.parents && Object.keys(c.parents).length > 0).length,
      withChildren: cases.filter(c => c.children && Object.keys(c.children).length > 0).length,
      withLocation: cases.filter(c => c.location).length,
    };
  }, [cases]);

  // Get case by ID helper
  const getCaseById = useCallback((caseId: string): Case | undefined => {
    return cases.find(c => c.id === caseId);
  }, [cases]);

  // Clear all errors
  const clearErrors = useCallback(() => {
    setError({});
  }, []);

  // Listen to cases collection
  useEffect(() => {
    if (!user || !account?.id) return;

    setError((prev) => ({ ...prev, list: null }));
    setLoading((prev) => ({ ...prev, list: true }));

    const casesQuery = query(
      collection(db, "cases"),
      where("accountId", "==", account.id),
      orderBy("identifier", "asc"),
    );

    const unsubscribe = onSnapshot(
      casesQuery,
      (snapshot) => {
        const caseList: Case[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data() as Case;
          caseList.push({ ...data, id: doc.id });
        });
        setCases(caseList);
        setLoading((prev) => ({ ...prev, list: false }));
      },
      (err) => {
        console.error("Error fetching cases:", err);
        setError((prev) => ({ ...prev, list: err }));
        setLoading((prev) => ({ ...prev, list: false }));
      },
    );

    return () => unsubscribe();
  }, [account?.id, user]);

  // Set selected case by ID
  const setSelectedCaseById = useCallback(async (caseId: string) => {
    if (!account?.id) return;

    setError((prev) => ({ ...prev, details: null }));
    setLoading((prev) => ({ ...prev, details: true }));

    try {
      const [caseData, parentList, childList, visitList, fileList, contractList] = await Promise.all([
        CaseService.getCase(caseId),
        CaseService.getCaseParents(caseId, account.id),
        CaseService.getCaseChildren(caseId, account.id),
        CaseService.getCaseVisits(caseId, account.id),
        CaseService.getCaseFiles(caseId),
        CaseService.getCaseContracts(caseId, account.id),
      ]);

      if (!caseData) {
        throw new Error("Case not found");
      }

      setSelectedCase(caseData);
      setParents(parentList);
      setChildList(childList);
      setVisits(visitList);
      setFiles(fileList);
      setContracts(contractList);
      setLoading((prev) => ({ ...prev, details: false }));
    } catch (err) {
      console.error("Error setting selected case:", err);
      setError((prev) => ({ ...prev, details: err as Error }));
      setLoading((prev) => ({ ...prev, details: false }));
    }
  }, [account?.id]);

  // Create case
  const createCase = useCallback(async (
    caseData: Omit<
      Case,
      | "id"
      | "createdAt"
      | "updatedAt"
      | "users"
      | "children"
      | "accountId"
      | "createdBy"
      | "status"
    >,
  ): Promise<string> => {
    if (!user?.uid || !account?.id) {
      throw new Error("User or account not available");
    }

    setError((prev) => ({ ...prev, create: null }));
    setLoading((prev) => ({ ...prev, create: true }));

    try {
      const newCaseId = await CaseService.createCase({
        ...caseData,
        accountId: account.id,
        createdBy: user.uid,
      });
      
      setLoading((prev) => ({ ...prev, create: false }));
      return newCaseId;
    } catch (err) {
      console.error("Error creating case:", err);
      setError((prev) => ({ ...prev, create: err as Error }));
      setLoading((prev) => ({ ...prev, create: false }));
      throw err;
    }
  }, [user?.uid, account?.id]);

  // Update case
  const updateCase = useCallback(async (
    caseId: string,
    updatedData: Partial<
      Omit<
        Case,
        | "id"
        | "createdAt"
        | "updatedAt"
        | "users"
        | "children"
        | "accountId"
        | "createdBy"
      >
    >,
  ): Promise<void> => {
    setError((prev) => ({ ...prev, update: null }));
    setLoading((prev) => ({ ...prev, update: true }));

    try {
      await CaseService.updateCase(caseId, updatedData);
      await setSelectedCaseById(caseId);
      setLoading((prev) => ({ ...prev, update: false }));
    } catch (err) {
      console.error("Error updating case:", err);
      setError((prev) => ({ ...prev, update: err as Error }));
      setLoading((prev) => ({ ...prev, update: false }));
      throw err;
    }
  }, [setSelectedCaseById]);

  // Delete case
  const deleteCaseById = useCallback(async (caseId: string): Promise<void> => {
    setError((prev) => ({ ...prev, delete: null }));
    setLoading((prev) => ({ ...prev, delete: true }));

    try {
      await CaseService.deleteCase(caseId);
      setLoading((prev) => ({ ...prev, delete: false }));
    } catch (err) {
      console.error("Error deleting case:", err);
      setError((prev) => ({ ...prev, delete: err as Error }));
      setLoading((prev) => ({ ...prev, delete: false }));
      throw err;
    }
  }, []);

  // Add file to case
  const addFileToCase = useCallback(async (
    caseId: string,
    fileData: Omit<
      File,
      "id" | "createdAt" | "updatedAt" | "accountId" | "createdBy"
    >,
  ): Promise<string> => {
    if (!user?.uid) {
      throw new Error("User not available");
    }

    try {
      const newFileId = await CaseService.addFileToCase(caseId, {
        ...fileData,
        createdBy: user.uid,
      });
      await setSelectedCaseById(caseId);
      return newFileId;
    } catch (err) {
      console.error("Error adding file to case:", err);
      throw err;
    }
  }, [user?.uid, setSelectedCaseById]);

  // Update file in case
  const updateFileInCase = useCallback(async (
    caseId: string,
    fileId: string,
    updatedData: Partial<
      Omit<File, "id" | "createdAt" | "updatedAt" | "accountId" | "createdBy">
    >,
  ): Promise<void> => {
    try {
      await CaseService.updateFileInCase(caseId, fileId, updatedData);
      await setSelectedCaseById(caseId);
    } catch (err) {
      console.error("Error updating file in case:", err);
      throw err;
    }
  }, [setSelectedCaseById]);

  // Delete file from case
  const deleteFileFromCase = useCallback(async (
    caseId: string,
    fileId: string,
  ): Promise<void> => {
    try {
      await CaseService.deleteFileFromCase(caseId, fileId);
      await setSelectedCaseById(caseId);
    } catch (err) {
      console.error("Error deleting file from case:", err);
      throw err;
    }
  }, [setSelectedCaseById]);

  // Remove parent from case
  const removeParentFromCase = useCallback(async (
    parentId: string,
  ): Promise<void> => {
    if (!selectedCase?.id) {
      throw new Error("No case selected");
    }

    try {
      await CaseService.removeParentFromCase(parentId);
      await setSelectedCaseById(selectedCase.id);
    } catch (err) {
      console.error("Error removing parent from case:", err);
      throw err;
    }
  }, [selectedCase?.id, setSelectedCaseById]);

  const contextValue = useMemo(() => ({
    loading,
    error,
    cases,
    selectedCase,
    files,
    visits,
    parents,
    childList,
    contracts,
    statistics,
    createCase,
    updateCase,
    deleteCaseById,
    addFileToCase,
    deleteFileFromCase,
    removeParentFromCase,
    setSelectedCase: setSelectedCaseById,
    updateFileInCase,
    clearErrors,
    getCaseById,
  }), [
    loading,
    error,
    cases,
    selectedCase,
    files,
    visits,
    parents,
    childList,
    contracts,
    statistics,
    createCase,
    updateCase,
    deleteCaseById,
    addFileToCase,
    deleteFileFromCase,
    removeParentFromCase,
    setSelectedCaseById,
    updateFileInCase,
    clearErrors,
    getCaseById,
  ]);

  return (
    <CaseContext.Provider value={contextValue}>
      {children}
    </CaseContext.Provider>
  );
};
